/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION (W-23754187 / W-23875749): read the production SEO slug path that the
 * `int_urlresolver_scapi` cartridge hooks stamp onto standard SCAPI responses as the `c_seoUrl`
 * custom attribute, and fold those into the `ProductUrlMap` / `CategoryUrlMap` shapes the storefront
 * link helpers already consume.
 *
 * WHY THIS EXISTS (hooks, not a custom API): we previously called a custom SCAPI `/resolve` endpoint
 * with a page's worth of ids — a blocking, waterfall round trip after the product/category fetch.
 * Per the 2026-08-17 alignment ("Hooks chosen for URL resolution … to ensure compatibility with
 * future platform support") the resolver now runs as `modifyGETResponse` hooks that enrich the
 * SAME product / product_search / category responses the loaders already fetch. So there is no extra
 * network call and the enriched response is edge-cacheable: reading the path is now a pure,
 * synchronous projection over data already in hand — which is all this module does.
 *
 * GRACEFUL DEGRADATION: `c_seoUrl` is ABSENT whenever the hook could not derive a canonical path
 * (or the cartridge is not yet deployed — the live deploy is deferred to 2026-08-18). A missing or
 * malformed value yields no map entry, and the link helpers fall back to their id-based routes
 * (`/product/:id` → 301 self-heal, `/category/:id`). So a resolver miss degrades to a working link,
 * never a broken one — the same contract the deleted `resolveCanonicalUrls` upheld.
 */
import type { ProductUrlMap } from '@/lib/product/product-utils';
import type { CategoryUrlMap } from '@/components/navigation-menu/category-href';

/**
 * Minimal structural view of any SCAPI document node that may carry the hook-injected attribute.
 * The generated ShopperProducts/ShopperSearch types model `c_*` custom attributes as an open
 * `& { [key: string]: unknown }` index, so reading `c_seoUrl` off a hit/category typechecks; we
 * still validate the runtime value here rather than trusting the type.
 */
type SeoUrlCarrier = { c_seoUrl?: unknown };

/**
 * Read and validate a hook-injected `c_seoUrl` off a SCAPI response node.
 *
 * Returns the path only when it is a non-empty, ROOT-RELATIVE string (starts with `/`). Rejecting
 * anything that isn't root-relative is a defense-in-depth guard: the cartridge hook already strips
 * the origin, but this ensures a stray absolute/`javascript:`/protocol-relative value from any
 * source can never become an href we emit.
 *
 * @param node - any object that might carry `c_seoUrl`
 * @returns the validated root-relative path, or `undefined`
 */
export function readSeoUrl(node: SeoUrlCarrier | null | undefined): string | undefined {
    if (!node || typeof node !== 'object') return undefined;
    const raw = (node as SeoUrlCarrier).c_seoUrl;
    if (typeof raw !== 'string') return undefined;
    const trimmed = raw.trim();
    // Root-relative only: must start with a single "/". This rejects "", "http://…", "javascript:…",
    // "../x", "category" AND protocol-relative "//host/…" (which starts with "/" but points off-origin).
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return undefined;
    // Reject the backslash-authority bypass and any smuggled control chars. A browser normalizes
    // "\" -> "/" for http(s), so "/\host" (or "/\\host") resolves off-origin exactly like the "//host"
    // case above, yet slips past the "//" check — the same family of bypasses the BM-redirect sanitizer
    // at _app.$.tsx defends against. A C0 control char (CR/LF/TAB) could likewise smuggle an off-origin
    // or header-splitting value into an href. A legitimate SEO slug path from the cartridge contains
    // neither, so reject outright and let the link helper fall back to its id route.
    // oxlint-disable-next-line no-control-regex -- reject smuggled controls in an href we emit
    if (/[\\\u0000-\u001f\u007f]/.test(trimmed)) return undefined;
    return trimmed;
}

/** A product-search hit as far as SEO-url mapping is concerned: an id plus the optional attribute. */
type SeoUrlHit = SeoUrlCarrier & { productId?: string | null };

/**
 * Build a `productId -> c_seoUrl` map from a list of product-search hits.
 * Hits with no id or no usable `c_seoUrl` are simply omitted (id-route fallback applies).
 *
 * @param hits - product-search hits from one or more result pages
 * @returns the product URL map (possibly empty)
 */
export function buildProductUrlMap(hits: ReadonlyArray<SeoUrlHit | null | undefined>): ProductUrlMap {
    const map: ProductUrlMap = {};
    for (const hit of hits) {
        const id = hit?.productId;
        if (!id) continue;
        const seoUrl = readSeoUrl(hit);
        if (seoUrl) map[id] = seoUrl;
    }
    return map;
}

/** A category tree node as far as SEO-url mapping is concerned. */
type SeoUrlCategoryNode = SeoUrlCarrier & {
    id?: string | null;
    categories?: ReadonlyArray<SeoUrlCategoryNode> | null;
};

/**
 * Walk a category tree (root + all nested `categories`) into an `id -> c_seoUrl` map.
 * Used for the mega-menu, whose links are keyed by category id.
 *
 * @param root - the root category document returned by the categories fetch
 * @param map - accumulator (internal; callers omit it)
 * @returns the category URL map (possibly empty)
 */
export function collectCategoryUrls(
    root: SeoUrlCategoryNode | null | undefined,
    map: CategoryUrlMap = {}
): CategoryUrlMap {
    if (!root) return map;
    const seoUrl = readSeoUrl(root);
    if (root.id && seoUrl) map[root.id] = seoUrl;
    for (const child of root.categories ?? []) {
        collectCategoryUrls(child, map);
    }
    return map;
}

/** The primary-category shape a PDP breadcrumb needs: the leaf plus its ancestor tree. */
export type SeoUrlPrimaryCategory = SeoUrlCategoryNode & {
    parentCategoryTree?: ReadonlyArray<SeoUrlCategoryNode> | null;
};

/**
 * The breadcrumb category chain for a PDP, root-first: the `parentCategoryTree` ancestors followed by
 * the leaf `primaryCategory` itself. This is the single definition of "which categories make up the
 * breadcrumb" — shared by the embedded-slug projection here and the nav-tree backfill in
 * `category-slug.server.ts`, so the two never drift out of order or scope.
 *
 * @param primaryCategory - `product.primaryCategory` (may be absent)
 * @returns the chain nodes root-first (empty when `primaryCategory` is absent)
 */
export function breadcrumbChainNodes(
    primaryCategory: SeoUrlPrimaryCategory | null | undefined
): ReadonlyArray<SeoUrlCategoryNode> {
    if (!primaryCategory) return [];
    return [...(primaryCategory.parentCategoryTree ?? []), primaryCategory];
}

/**
 * Build the breadcrumb `id -> c_seoUrl` map from a product's embedded `primaryCategory`: its
 * ancestor chain (`parentCategoryTree`) plus the leaf category itself. The product-response hook
 * enriches these embedded nodes, so no extra fetch is needed.
 *
 * @param primaryCategory - `product.primaryCategory` (may be absent)
 * @returns the category URL map for the breadcrumb chain (possibly empty)
 */
export function collectBreadcrumbCategoryUrls(
    primaryCategory: SeoUrlPrimaryCategory | null | undefined
): CategoryUrlMap {
    const map: CategoryUrlMap = {};
    for (const node of breadcrumbChainNodes(primaryCategory)) {
        const seoUrl = readSeoUrl(node);
        if (node?.id && seoUrl) map[node.id] = seoUrl;
    }
    return map;
}

/** A product node as far as breadcrumb-URL mapping is concerned: its embedded `primaryCategory`. */
type ProductBreadcrumbCarrier = {
    primaryCategory?: SeoUrlPrimaryCategory | null;
};

/**
 * Build the breadcrumb `id -> c_seoUrl` map for a PDP straight off the ENRICHED PRODUCT RESPONSE — no
 * extra fetch (Kevin He review, PR #107 / W-23875749).
 *
 * The breadcrumb slugs ride the SAME single product GET the PDP already issues: the loader expands
 * `primary_category` (Steffen's #110, for the breadcrumb structure/labels), and the
 * `int_urlresolver_scapi` product hook stamps `c_seoUrl` onto every node of that embedded
 * `primaryCategory` object — the leaf and each `parentCategoryTree` ancestor. This keeps the
 * implementation close to OOTB (the same `c_seoUrl` convention as the mega-menu category tree) rather
 * than a bespoke top-level flattened attribute. Verified on bjgk-006: the embedded `c_seoUrl` values
 * DO serialize under the `primary_category` expand. A chain id with no usable slug is omitted and
 * `CategoryBreadcrumbs` falls back to `/category/:id`.
 *
 * @param product - `loaderData.product` (the enriched Shopper Products document; may be absent)
 * @returns the breadcrumb `id -> c_seoUrl` map (possibly empty / partial)
 */
export function collectProductBreadcrumbCategoryUrls(
    product: ProductBreadcrumbCarrier | null | undefined
): CategoryUrlMap {
    return collectBreadcrumbCategoryUrls(product?.primaryCategory);
}
